import { useEffect, useState } from "react";
import { Container, Paper, Typography, Box, Divider } from "@mui/material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getCsrfToken } from "../utils/csrf";
import {
  groupTermsByHeading,
  STANDARD_SECTION_COUNT,
} from "../utils/additionalTerms";

const TermsPage = () => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const mainColor = "#380045";
  const accentColor = "#D4AF37";

  const [additionalTerms, setAdditionalTerms] = useState([]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // When the page is opened from a quote (/terms/:quoteId), pull in any
  // clauses recorded against that specific quote. Every failure path here is
  // silent on purpose: a customer must never be shown an error in place of
  // the terms they are being asked to accept, so we fall back to the standard
  // terms alone.
  useEffect(() => {
    if (!quoteId) {
      setAdditionalTerms([]);
      return undefined;
    }

    let cancelled = false;

    const fetchAdditionalTerms = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_BACKEND_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken() || "",
          },
          credentials: "include",
          body: JSON.stringify({
            query: `
              query GetQuoteTerms($uuid: ID!) {
                getQuote(uuid: $uuid) {
                  additionalTerms {
                    id
                    sectionTitle
                    body
                  }
                }
              }
            `,
            variables: { uuid: quoteId },
          }),
        });

        const result = await response.json();
        if (cancelled || result.errors) return;

        // groupTermsByHeading tolerates null/blank clauses, so pass through.
        setAdditionalTerms(result.data?.getQuote?.additionalTerms ?? []);
      } catch (err) {
        // Swallow — the standard terms are already on screen.
      }
    };

    fetchAdditionalTerms();

    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  const additionalGroups = groupTermsByHeading(additionalTerms);

  const Section = ({ number, title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{
          color: mainColor,
          fontWeight: 600,
          mb: 2,
        }}
      >
        {number}. {title}
      </Typography>
      {children}
    </Box>
  );

  const Clause = ({ number, children, sx }) => (
    <Typography
      variant="body1"
      sx={{
        mb: 2,
        pl: 2,
        color: "#444",
        lineHeight: 1.8,
        ...sx,
      }}
    >
      <strong>{number}</strong> {children}
    </Typography>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#faf9f6",
        py: 6,
      }}
    >
      <Helmet>
        <title>Terms and Conditions | Imperial Drummers</title>
      </Helmet>

      <Container maxWidth="md">
        <Box
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              window.close();
            }
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: mainColor,
            cursor: "pointer",
            mb: 3,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">Back</Typography>
        </Box>

        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "24px",
            boxShadow: "0 20px 60px rgba(56,0,69,0.08)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              variant="h3"
              sx={{
                color: mainColor,
                fontWeight: 700,
              }}
            >
              Terms and Conditions
            </Typography>
            <Divider
              sx={{
                width: 60,
                mx: "auto",
                mt: 2,
                borderColor: accentColor,
                borderWidth: 2,
              }}
            />
          </Box>

          <Section number="1" title="Delays to Schedule">
            <Clause number="1.1">
              Imperial Drummers take into account that functions may run late.
              To accommodate for this, Imperial Drummers will give you an hour
              grace period from the start of the booking time. If the dhol
              drummers are still kept waiting after this grace period of an
              hour, you will incur an additional charge of £50 per drummer. The
              late fee must be paid in cash on the day of the booking in full
              amount.
            </Clause>
            <Clause number="1.2">
              Imperial Drummers reserve the right to terminate the booking if
              they are kept waiting for more than two hours past the stated
              start of booking time. The waiting charges (as stated in section
              1.1) and booking fee will still be applicable.
            </Clause>
          </Section>

          <Section number="2" title="Weather Conditions">
            <Clause number="2.1">
              Bookings that require our drummers to perform outdoors will be
              dependent on the weather. Our dhol drummers reserve the right not
              to perform outdoors if the weather conditions are such that they
              could cause damage to our drums. (E.g. raining, snowing etc.). In
              these circumstances, you will still be charged for the booking
              however we will do our best to accommodate you by playing indoors.
            </Clause>
            <Clause number="2.2">
              Any damage caused to the drums of our performers at the fault of
              guests/family will be billed and charged for damages.
            </Clause>
          </Section>

          <Section number="3" title="Changes & Cancellation">
            <Clause number="3.1">
              Imperial Drummers cannot guarantee that all changes to a booking
              that you request will be accommodated however we shall try our
              best to do so. If you require our drummers to perform again when
              it is not stated on this contract, then an extra charge will
              occur.
            </Clause>
            <Clause number="3.2">
              If Imperial Drummers encounter any inappropriate behavior or
              verbal abuse from a guest or family member at the function, then
              the drummers reserve the right to terminate and leave the booking
              at their own discretion. The full payment and late fee must still
              be paid in full by cash.
            </Clause>
          </Section>

          <Section number="4" title="Payment">
            <Clause number="4.1">
              The full payment owed to Imperial Drummers must be paid in cash on
              the day. Alternatively, if you prefer a full bank transfer this
              can be done one week prior to the function taking place. (Speak to
              the Imperial Drummers Team regarding this)
            </Clause>
            <Clause number="4.2">
              Any tip money given to Dhol drummers while performing belongs to
              Imperial Drummers.
            </Clause>
          </Section>

          <Section number="5" title="Deposit Policy">
            <Clause number="5.1">
              A non-refundable retainer is required to secure the booking date.
              This deposit reserves the musicians' availability and covers
              administrative and preparatory work.
            </Clause>
            <Clause number="5.2">
              In the event of cancellation by the Client for any reason, the
              deposit will be retained by the Musician(s).
            </Clause>
            <Clause number="5.3">
              <strong>Exception:</strong> If the Musician(s) are able to secure
              a replacement booking of equal value for the same date and time,
              the deposit (or a proportion thereof) will be refunded, less any
              reasonable administrative costs.
            </Clause>
          </Section>

          <Section number="6" title="Last-Minute Cancellation">
            <Clause number="6.1">
              If the Client cancels the booking 7 days or fewer before the
              scheduled performance date, the Client agrees to pay the full
              agreed fee. This is to cover the Musician(s)' lost opportunity,
              preparation, and potential inability to rebook the date.
            </Clause>
            <Clause number="6.2">
              The non-refundable retainer will be deducted from the total fee,
              with the remaining balance due immediately.
            </Clause>
            <Clause number="6.3">
              All cancellations must be submitted in writing to be considered
              valid.
            </Clause>
          </Section>

          {additionalGroups.length > 0 && (
            <Box
              sx={{
                mt: 5,
                p: { xs: 2.5, md: 3.5 },
                borderLeft: `4px solid ${accentColor}`,
                borderRadius: "0 12px 12px 0",
                backgroundColor: "rgba(212,175,55,0.06)",
                // Section carries a bottom margin for spacing between
                // sections; drop it on the last one so the panel closes tight.
                "& > :last-child": { mb: 0 },
                "& > :last-child > :last-child": { mb: 0 },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: mainColor, fontWeight: 600, mb: 1 }}
              >
                Additional Terms for Your Booking
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#555", mb: 3, lineHeight: 1.7 }}
              >
                The following terms have been agreed specifically for your
                booking and apply in addition to the terms above.
              </Typography>

              {additionalGroups.map((group, groupIndex) => {
                const sectionNumber = STANDARD_SECTION_COUNT + groupIndex + 1;
                return (
                  <Section
                    key={group.clauses[0].id ?? groupIndex}
                    number={sectionNumber}
                    title={group.title || "Additional Terms"}
                  >
                    {group.clauses.map((clause, clauseIndex) => (
                      <Clause
                        key={clause.id ?? clauseIndex}
                        number={`${sectionNumber}.${clauseIndex + 1}`}
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {clause.body}
                      </Clause>
                    ))}
                  </Section>
                );
              })}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsPage;
